import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import type { ComponentType } from "react";

import { nativeCard } from "../cards";
import { ChartCard } from "../charts/ChartCard";
import { ApprovalCard } from "../choices/ApprovalCard";
import { PickCard } from "../choices/PickCard";
import { INSPECTABLE_TOOLS, SandboxInspectButton } from "../inspector/SandboxInspector";
import { PreviewFrame } from "../preview/PreviewFrame";
import type { ChoiceKind } from "../protocol/protocol";
import { resultCard } from "../results";
import type { ToolArtifact } from "./convert";
import { StepCard } from "./StepCard";
import { ToolLine } from "./ToolLine";
import { toolLine } from "./toolLines";

/**
 * The card each choice kind renders in the thread, at the call that raised it. A filing has none:
 * it lives on the composer (FilingPopup), so its call reads as a plain line.
 */
const CHOICE_CARDS: Record<ChoiceKind, ComponentType<{ choiceRef: string }> | null> = {
  approval: ApprovalCard,
  pick: PickCard,
  filing: null,
};

/** The card a tool part shows instead of a line, if its choice has one. */
function choiceCard(artifact: ToolArtifact | undefined) {
  return artifact?.choiceKind && artifact.choiceRef ? CHOICE_CARDS[artifact.choiceKind] : null;
}

/** Whether a tool part renders as a card rather than a line (the thread keeps cards out of step groups). */
export function showsCard(artifact: unknown, result?: unknown): boolean {
  const tool = artifact as ToolArtifact | undefined;
  return (
    nativeCard(tool, result) !== null ||
    choiceCard(tool) !== null ||
    Boolean(tool?.chart) ||
    Boolean(tool?.preview) ||
    tool?.showsResult === true
  );
}

/**
 * Every tool-call part renders here (assistant-ui's `tools.Override`), whatever the tool: a native
 * card when its ToolUi kind has one (`NATIVE_CARDS`), a choice as its card (each reads its choice
 * from the store by ref), a chart it showed as the chart card, a sandbox call as its line with its
 * step card (its line carrying the same icon), a preview as its line and then its frame, anything
 * else as its ToolLine (H2), with its result's card under it when the read has one (`results/`). A
 * stored repl or bash call carries the dev inspector's icon (off outside local development); a live
 * part's name is not known yet.
 */
export const AgentToolPart: ToolCallMessagePartComponent = (props) => {
  const artifact = props.artifact as ToolArtifact | undefined;
  const native = nativeCard(artifact, props.result);
  if (native && artifact) {
    return <native.Card args={props.args} result={props.result} artifact={artifact} />;
  }
  const Card = choiceCard(artifact);
  if (Card && artifact?.choiceRef) return <Card choiceRef={artifact.choiceRef} />;
  if (artifact?.chart) return <ChartCard part={artifact.chart} />;
  const inspect = INSPECTABLE_TOOLS.has(props.toolName) && (
    <SandboxInspectButton callId={props.toolCallId} />
  );
  if (artifact?.step) {
    return (
      <StepCard line={artifact.line} state={artifact.state} step={artifact.step}>
        {inspect}
      </StepCard>
    );
  }
  const line = (
    <ToolLine
      line={artifact?.line ?? toolLine("running", null)}
      state={artifact?.state ?? "running"}
      sources={artifact?.sources}
    >
      {inspect}
    </ToolLine>
  );
  const card = artifact?.showsResult ? resultCard(props.toolName, props.result) : null;
  const preview = artifact?.preview;
  if (!card && !preview) return line;
  return (
    <div className="flex flex-col gap-2">
      {line}
      {card}
      {preview && <PreviewFrame workspace={preview.workspace} path={preview.path} />}
    </div>
  );
};
